/**
 * Where a running Herbereken is: a share of the whole for the button and the
 * dialog's bar, and the four steps the dialog lists. The backend reports the
 * job's progress per stage: Odoo read table by table, the data checked, the
 * pages built part by part, and the sections stored in batches.
 */

import type { AppJob, JobProgress } from "@/lib/api";

/** The stages of a recompute, in the order they run. */
export const RECOMPUTE_STAGES = ["odoo_read", "check", "build", "store"] as const;
export type RecomputeStage = (typeof RECOMPUTE_STAGES)[number];

/**
 * Each stage's share of the whole in hundredths, after its usual time in
 * production: reading Odoo takes most. Whole numbers, so the sum of the
 * stages before the last plus the last one comes to exactly 100.
 */
const STAGE_WEIGHTS: Readonly<Record<RecomputeStage, number>> = {
  odoo_read: 60,
  check: 2,
  build: 30,
  store: 8,
};

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

export function isRecomputeStage(stage: string): stage is RecomputeStage {
  return (RECOMPUTE_STAGES as readonly string[]).includes(stage);
}

/**
 * How far the current stage is, 0 to 1: its finished steps, plus the share of
 * the table being read when Odoo says how many records it holds.
 */
export function stageFraction(progress: JobProgress): number {
  if (progress.total <= 0) {
    return 0;
  }
  const { item_done: itemDone, item_total: itemTotal } = progress;
  const item =
    progress.stage === "odoo_read" && itemDone !== null && itemTotal !== null && itemTotal > 0
      ? clamp01(itemDone / itemTotal)
      : 0;
  return clamp01((progress.done + item) / progress.total);
}

/** How far the whole recompute is, 0 to 1; null when its progress is unknown. */
export function progressFraction(progress: JobProgress | null | undefined): number | null {
  if (!progress || !isRecomputeStage(progress.stage)) {
    return null;
  }
  let before = 0;
  for (const stage of RECOMPUTE_STAGES) {
    if (stage === progress.stage) {
      break;
    }
    before += STAGE_WEIGHTS[stage];
  }
  return clamp01((before + STAGE_WEIGHTS[progress.stage] * stageFraction(progress)) / 100);
}

/** A share as whole percents, never rounded up, so a recompute shows 100 only once it is done. */
export function wholePercent(fraction: number): number {
  return Math.floor(clamp01(fraction) * 100 + 1e-9);
}

export type RecomputeOutcome = "running" | "complete" | "failed";

export function recomputeOutcome(job: Pick<AppJob, "status"> | null): RecomputeOutcome {
  return job?.status === "complete" || job?.status === "failed" ? job.status : "running";
}

export type StepStatus = "pending" | "active" | "done" | "failed";

export interface RecomputeStep {
  readonly stage: RecomputeStage;
  readonly status: StepStatus;
  /** The active step's progress, for its detail and its bar; null for every other step. */
  readonly progress: JobProgress | null;
}

/** The stage a failure names. Any other failure (something unexpected, a stop) keeps the stage last seen. */
const ERROR_STAGES: Readonly<Record<string, RecomputeStage>> = {
  odoo_not_configured: "odoo_read",
  odoo_unavailable: "odoo_read",
  odoo_access_refused: "odoo_read",
  odoo_field_missing: "odoo_read",
  odoo_time_budget: "odoo_read",
  odoo_data_missing: "check",
  snapshot_invalid: "build",
  store_failed: "store",
};

/**
 * The step a failed recompute stopped in: the one its error names, else the
 * one last seen running (a job that is over reports no progress), else the first.
 */
export function failedStage(error: string | null, lastSeen: JobProgress | null): RecomputeStage {
  if (error !== null && Object.hasOwn(ERROR_STAGES, error)) {
    return ERROR_STAGES[error] ?? "odoo_read";
  }
  return lastSeen && isRecomputeStage(lastSeen.stage) ? lastSeen.stage : "odoo_read";
}

/**
 * Where the bar of a failed recompute stops: how far it was last seen when
 * that is the step it failed in, else the start of that step.
 */
export function failedFraction(error: string | null, lastSeen: JobProgress | null): number {
  const stage = failedStage(error, lastSeen);
  if (lastSeen?.stage === stage) {
    return progressFraction(lastSeen) ?? 0;
  }
  return (
    progressFraction({
      stage,
      done: 0,
      total: 1,
      item: null,
      item_done: null,
      item_total: null,
      stage_started_at: "",
    }) ?? 0
  );
}

/**
 * The four steps as the dialog shows them. While it runs, the steps before the
 * current stage are done and the ones after it pending; without any progress
 * yet (or from a job that runs outside this server process) all are pending.
 */
export function recomputeSteps(
  outcome: RecomputeOutcome,
  progress: JobProgress | null,
  error: string | null = null,
): readonly RecomputeStep[] {
  const at =
    outcome === "failed"
      ? failedStage(error, progress)
      : outcome === "running" && progress && isRecomputeStage(progress.stage)
        ? progress.stage
        : null;
  const index = at === null ? -1 : RECOMPUTE_STAGES.indexOf(at);
  return RECOMPUTE_STAGES.map((stage, i): RecomputeStep => {
    if (outcome === "complete" || (index >= 0 && i < index)) {
      return { stage, status: "done", progress: null };
    }
    if (i === index) {
      return outcome === "failed"
        ? { stage, status: "failed", progress: null }
        : { stage, status: "active", progress };
    }
    return { stage, status: "pending", progress: null };
  });
}

/** Elapsed time as minutes and seconds, "0:07", "1:42". */
export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
